/**
 * Money and date helpers shared by the billing screens (Properties & plans, Invoices, Users, the late-payment banner).
 * Amounts arrive as integer minor units together with their currency code; nothing here assumes a currency.
 */

/** "AED 1,575.00": currency code, a space, then the grouped amount with two decimals. No code when none is given. */
export function formatMoney(cents, currency) {
  const amount = (Number(cents) || 0) / 100;
  const text = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const signed = amount < 0 ? `-${text}` : text;
  return currency ? `${String(currency).toUpperCase()} ${signed}` : signed;
}

// The customer's display currency (Admin > Branding). Set once the customer's settings load; AED until then.
let displayCurrency = 'AED';

export function setDisplayCurrency(code) {
  if (/^[A-Z]{3}$/.test(String(code))) displayCurrency = code;
}

/** The customer's currency code, for column headings and field labels such as "Unit rate (AED)". */
export const currencyCode = () => displayCurrency;

/** An amount in currency units (not minor units) in the customer's currency: "AED 1,234.50"; `decimals: 0` for whole amounts. */
export function formatAmount(amount, { decimals = 2 } = {}) {
  const value = Number(amount) || 0;
  const text = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return `${displayCurrency} ${value < 0 ? '-' : ''}${text}`;
}

/** A rate held in basis points as a percentage: 500 -> "5%", 525 -> "5.25%". */
export function formatRate(bps) {
  return `${Number(((Number(bps) || 0) / 100).toFixed(2))}%`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Calendar date (YYYY-MM-DD) or timestamp as "25 Oct 2026"; `{ year: false }` leaves the year off.
 * Everything is read as UTC so a plain date never shifts a day, and the month names do not depend on the browser.
 */
export function formatDate(iso, { year = true } = {}) {
  if (!iso) return '';
  const date = new Date(String(iso).length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(date.getTime())) return '';
  const text = `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
  return year ? `${text} ${date.getUTCFullYear()}` : text;
}

/** Whole days from today (local calendar) until the YYYY-MM-DD date `iso`; negative once the date has passed. */
export function daysUntil(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  const now = new Date();
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
}
